import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import type { RouteObject } from 'react-router';

import { ErrorBoundary } from './ErrorBoundary';
import { Root } from './client/Root';
import { ArticlePage, Page, PageWithoutSection } from './client/pages';
import { ArticlesCategory } from './client/pages/ArticlesCategory';
import { Author } from './client/pages/Author';
import { PreviewPage } from './client/pages/Page/PreviewPage';
import { SearchResults } from './client/pages/SearchResults';
import type { ConfigStateContext } from './client/shared/hooks/useConfig';
import {
  articleLoader,
  authorWithArticlesLoader,
  categoryLoader,
  pageLoader,
  pageWithoutSectionLoader,
  previewDocumentLoader,
  sectionsLoader,
} from './loaders';
import { PATHS } from './paths';
import { notFound } from './responses';

export type RouteProps = {
  client: ApolloClient<NormalizedCacheObject>;
  clientTAL: ApolloClient<NormalizedCacheObject>;
  config: ConfigStateContext;
};

// Centralized loader wrapper to filter out slugs params containing a dot (for non-media routes)
const filterInvalidSlug =
  (loader: any): any =>
  async (args: any) => {
    const slug = args.params?.slug;
    if (slug && slug.includes('.')) {
      throw notFound(slug);
    }
    return loader(args);
  };

export const makeRoutes = ({
  config,
  client,
  clientTAL,
}: RouteProps): RouteObject[] => {
  const configProps = { config, client, clientTAL };
  const children = makeChildRoutes(configProps);

  return [
    {
      /**
       * This route added to support the language param in the URL.
       * Note: we are not validating that the dynamic param is a language. /xy and /en would both work in this current state.
       * The validation could be added to this route in the future.
       * @ref https://stackoverflow.com/questions/73738776/restricting-dynamic-segments-to-certain-values-in-react-router-v6
       */
      path: PATHS.LANGUAGE,
      errorElement: <ErrorBoundary />,
      element: <Root client={client} clientTAL={clientTAL} config={config} />,
      children: [
        {
          path: 'article-search/:term/:pageNumber?',
          element: <SearchResults />,
        },
        {
          path: 'about/search/:term/:pageNumber?',
          element: <SearchResults />,
          loader: filterInvalidSlug(sectionsLoader(configProps)),
        },
        {
          path: 'careers/life-at-seek/search/:term/:pageNumber?',
          element: <SearchResults />,
          loader: filterInvalidSlug(sectionsLoader(configProps)),
        },
        {
          path: 'author/:slug/:pageNumber?',
          element: <Author />,
          loader: filterInvalidSlug(authorWithArticlesLoader(configProps)),
        },
        {
          // Pages that have no section assigned are served from /page/<page-slug>
          path: PATHS.PAGE,
          children: [
            {
              index: true,
              element: <PageWithoutSection />,
              loader: filterInvalidSlug(pageWithoutSectionLoader(configProps)),
            },
            {
              path: ':slug',
              element: <PageWithoutSection />,
              loader: filterInvalidSlug(pageWithoutSectionLoader(configProps)),
            },
            {
              path: 'view/:id',
              element: <PreviewPage />,
              loader: filterInvalidSlug(previewDocumentLoader(configProps)),
            },
            {
              path: 'preview/:id',
              element: <PreviewPage isDraft={true} />,
              loader: filterInvalidSlug(previewDocumentLoader(configProps)),
            },
          ],
        },
        {
          path: ':section/*',
          children,
          loader: filterInvalidSlug(sectionsLoader(configProps)),
        },
        {
          path: 'career-advice/article-preview/:id',
          element: (
            <ArticlePage
              site={config.site}
              section={config.section}
              isPreview={true}
            />
          ),
          loader: filterInvalidSlug(articleLoader(configProps, true)),
        },
        // Setting an exception for security-hub, to allow slug to match last section
        // and allow both candidate and employer to have the same slug
        {
          path: 'security-hub',
          element: <Page />,
          loader: filterInvalidSlug(
            pageLoader({
              ...configProps,
              config: {
                ...configProps.config,
                routeArray: ['security-hub', 'security-hub'],
              },
            }),
          ),
        },
      ],
    },
  ];
};

/**
 * These routes are shared across all root routes
 */
const makeChildRoutes = ({
  config,
  client,
  clientTAL,
}: RouteProps): RouteObject[] => {
  const configProps = { config, client, clientTAL };
  return [
    {
      path: 'article/:slug',
      element: (
        <ArticlePage
          site={config.site}
          section={config.section}
          isPreview={false}
        />
      ),
      loader: filterInvalidSlug(articleLoader(configProps)),
    },
    {
      path: 'category/:slug/:pageNumber?',
      element: <ArticlesCategory />,
      loader: filterInvalidSlug(categoryLoader(configProps)),
    },
    {
      path: ':subsection',
      children: [
        {
          path: 'article/:slug',
          element: (
            <ArticlePage
              site={config.site}
              section={config.section}
              isPreview={false}
            />
          ),
          loader: filterInvalidSlug(articleLoader(configProps)),
        },
        {
          path: 'category/:slug/:pageNumber?',
          element: <ArticlesCategory />,
          loader: filterInvalidSlug(categoryLoader(configProps)),
        },
        {
          path: '*',
          element: <Page />,
          loader: filterInvalidSlug(pageLoader(configProps)),
        },
      ],
    },
    {
      path: ':slug?',
      element: <Page />,
      loader: filterInvalidSlug(pageLoader(configProps)),
    },
  ];
};
